import React, { useEffect, useState } from 'react';
import styles from './CarouselBlock.module.scss';
import { CarouselBlockFragment } from '@src/graphql/gql-types';
import CommunityImageCard from '../CommunityImageCard';
import Heading from '@src/elements/Heading';
import { Carousel } from 'react-responsive-carousel';
import ButtonIcon from '../Button/ButtonIcon/ButtonIcon';
import {
  COLOR_OUTERSPACE,
  ContentBlockBackgroundColors
} from '@src/types/colors';
import { Container } from '@src/elements/Grid';
import { useViewport } from '@src/hooks/useViewport';
import { getColorClasses } from '@src/utils/colorClasses';
import classNames from 'classnames';
import { LG_BREAKPOINT } from '@src/constants/breakpoints';

type CarouselBlock = CarouselBlockFragment & {
  backgroundColor?: ContentBlockBackgroundColors;
};

const CarouselBlock: React.FC<CarouselBlock> = ({
  tagline,
  heading,
  description,
  carouselItemsCollection,
  backgroundColor
}) => {
  const [selected, setSelected] = useState(0);
  const [cardWidth, setCardWidth] = useState(315);

  const { width } = useViewport();

  useEffect(() => {
    if (width <= 420) {
      setCardWidth(width - 64);
    } else if (width >= LG_BREAKPOINT) {
      setCardWidth(384);
    } else {
      setCardWidth(315);
    }
  }, [width]);

  const previousItem = () => {
    if (selected === 0) {
      setSelected((carouselItemsCollection?.items?.length || 0) - 1);
    } else {
      setSelected(selected - 1);
    }
  };

  const nextItem = () => {
    if (selected === (carouselItemsCollection?.items?.length || 0) - 1) {
      setSelected(0);
    } else {
      setSelected(selected + 1);
    }
  };

  return (
    <div
      className={classNames(
        styles.carouselBlock,
        backgroundColor ? getColorClasses(backgroundColor, styles) : ''
      )}
    >
      <Container containerWidth="fluid">
        <div className={styles.tagline}>{tagline}</div>
        <Heading priority={2} fontStyle="h2">
          {heading}
        </Heading>
        <p className={styles.description}>{description}</p>
      </Container>

      <Container className={styles.carouselContainer}>
        <Carousel
          showArrows={false}
          showStatus={false}
          showIndicators={false}
          preventMovementUntilSwipeScrollTolerance={true}
          swipeScrollTolerance={48}
          showThumbs={false}
          selectedItem={selected}
          onChange={setSelected}
          width={cardWidth}
        >
          {carouselItemsCollection?.items.map((item) => (
            <div className={styles.carouselItem} key={item?.heading}>
              {item?.__typename === 'CommunityImageCard' && (
                <CommunityImageCard {...item} />
              )}
            </div>
          ))}
        </Carousel>
      </Container>
      {carouselItemsCollection && carouselItemsCollection?.items.length > 2 && (
        <Container>
          <div className={styles.buttonContainerWrapper}>
            <div className={styles.buttonContainer}>
              <ButtonIcon
                boxIcon="left-arrow-alt"
                color={COLOR_OUTERSPACE}
                fillVariant="filled"
                onClick={previousItem}
                aria-label="Previous Item"
              />
              <ButtonIcon
                boxIcon="right-arrow-alt"
                color={COLOR_OUTERSPACE}
                fillVariant="filled"
                onClick={nextItem}
                aria-label="Next Item"
              />
            </div>
          </div>
        </Container>
      )}
    </div>
  );
};

export default CarouselBlock;
